import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * LAYOUT PRIMITIVES
 *
 * Stack (vertical), Inline (horizontal), Grid (columns) and Center exist so
 * spacing is chosen from a named scale instead of whatever `space-y-*` value
 * happened to be typed. Two screens using `gap="md"` will always match; two
 * using `space-y-3` and `space-y-4` will not.
 *
 * They share one gap scale and one alignment vocabulary, so swapping a Stack
 * for an Inline changes the direction and nothing else. The **defaults**
 * differ on purpose — a column of form fields wants more air than a row of
 * badges — and that is the only difference between them.
 */

/**
 * One justify vocabulary for every layout primitive.
 *
 * Inline used to offer `around` and Stack did not, for no reason anyone could
 * name — `justify-around` works the same in a column. An option that exists on
 * one direction and not the other is a thing to trip over, not a feature.
 */
const justifyVariants = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
} as const;

const wrapVariants = { true: 'flex-wrap', false: 'flex-nowrap' } as const;

/**
 * The child that takes the leftover room *and* is allowed to shrink.
 *
 * `flex-1` on its own is not enough: a flex child's minimum width is its
 * content, so a long name inside it pushes the row wide instead of
 * truncating. `min-w-0` is the half everyone forgets, and the app wrote the
 * pair out by hand 33 times — every one of them a Stack or an Inline that
 * needed to be told it was the growing child.
 */
const growVariants = { true: 'min-w-0 flex-1', false: '' } as const;

/**
 * A box whose size or place changes with state, and animates the change.
 *
 * The dashboard shell has three of these — the navigation column that
 * narrows to a rail, the settings sidebar that slides in beside it, and the
 * row being dragged in the sidebar's edit mode — and each spelled its own
 * duration and easing. One motion, the same `duration-slow` MasterDetail's
 * panes use, and off entirely for anyone who asked their OS for less motion.
 */
const animatedVariants = {
  true: 'transition-all duration-slow ease-in-out motion-reduce:transition-none',
  false: '',
} as const;

/**
 * A row the dragged item can land on.
 *
 * The sidebar's edit mode reorders its rows by drag and drop, and marks the
 * one the drop will land above with a rule along its top edge. Three states,
 * not two: `true` draws the rule, `false` reserves its space in the row's own
 * background so nothing shifts as the pointer crosses it, and unset — every
 * Stack that is not a drop target — draws nothing at all.
 */
const dropTargetVariants = {
  true: 'border-t-2 border-primary bg-primary/5',
  false: 'border-t-2 border-transparent',
} as const;

/**
 * Shared gap scale for every layout primitive, named by its 4px-grid step.
 *
 * This was a t-shirt scale — none/xs/sm/md/lg/xl — until 179 places in the
 * product needed 12px, which sat between `sm` and `md` with no name available.
 * That is the failure mode of t-shirt sizing: inserting a step means either a
 * word that carries no ordering (`snug`? bigger or smaller than `md`?) or
 * renaming everything downstream.
 *
 * Numbers do not have that problem. `gap={3}` is 12px, it is obviously between
 * 2 and 4, and the next value anyone needs already has a name. The numbers are
 * Tailwind's own spacing steps, so there is nothing extra to learn.
 */
const gapVariants = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  6: 'gap-6',
  8: 'gap-8',
} as const;

export const stackRecipe = cva([uiMarker, uiMarkerLayout, 'flex flex-col'], {
  variants: {
    gap: gapVariants,
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    },
    justify: justifyVariants,
    /** Let items wrap rather than overflow. Rare in a column, but symmetric. */
    wrap: wrapVariants,
    grow: growVariants,
    /**
     * A rule between each child. For lists of rows — invoices, documents,
     * specs — where the app was writing `divide-y` onto a Stack or a bare div.
     * The rule is drawn on the child's top edge, so it only reads as *between*
     * the rows when the gap is 0; the component defaults it there.
     */
    divided: { true: 'divide-y divide-border', false: '' },
    animated: animatedVariants,
    dropTarget: dropTargetVariants,
  },
  defaultVariants: {
    gap: 4,
    align: 'stretch',
    justify: 'start',
    wrap: false,
    grow: false,
    divided: false,
    animated: false,
  },
});

export const inlineRecipe = cva([uiMarker, uiMarkerLayout, 'flex flex-row'], {
  variants: {
    gap: gapVariants,
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      baseline: 'items-baseline',
      stretch: 'items-stretch',
    },
    justify: justifyVariants,
    /** Let items wrap onto a second line rather than overflow. */
    wrap: wrapVariants,
    grow: growVariants,
    animated: animatedVariants,
    dropTarget: dropTargetVariants,
  },
  defaultVariants: {
    gap: 2,
    align: 'center',
    justify: 'start',
    wrap: false,
    grow: false,
    animated: false,
  },
});

/**
 * Responsive visibility, written out per breakpoint and display type.
 *
 * A table for the reason Grid's column classes are: Tailwind reads class
 * names from source, so `${bp}:inline` produces nothing. `below` hides from
 * the breakpoint up and needs no display class of its own — the element keeps
 * whatever it renders as until then.
 */
export const SHOW_CLASSES = {
  above: {
    inline: {
      sm: 'hidden sm:inline',
      md: 'hidden md:inline',
      lg: 'hidden lg:inline',
    },
    block: {
      sm: 'hidden sm:block',
      md: 'hidden md:block',
      lg: 'hidden lg:block',
    },
    flex: { sm: 'hidden sm:flex', md: 'hidden md:flex', lg: 'hidden lg:flex' },
  },
  below: { sm: 'sm:hidden', md: 'md:hidden', lg: 'lg:hidden' },
} as const;

export type ShowBreakpoint = keyof typeof SHOW_CLASSES.below;
export type ShowDisplay = keyof typeof SHOW_CLASSES.above;

/**
 * The frame around a screen that is not inside the dashboard shell — sign-in,
 * sign-up, an invitation, an opt-in, the account-error page.
 *
 * Five files had this shape, on four different backgrounds (`bg-background`,
 * `bg-muted/50`, `bg-brand-100`, `bg-canvas`), which is the whole argument for
 * naming it: they were meant to be the same place.
 */
export const publicShellRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'flex min-h-screen w-full flex-col items-center justify-center p-4',
  ],
  {
    variants: {
      tone: {
        canvas: 'bg-canvas',
        muted: 'bg-muted/50',
        background: 'bg-background',
      },
    },
    defaultVariants: { tone: 'canvas' },
  }
);

/** The column the content sits in. Every one of the five files chose max-w-md. */
export const publicShellBodyRecipe = cva([uiMarker, uiMarkerLayout, 'w-full'], {
  variants: {
    width: {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-3xl',
      full: 'max-w-7xl',
    },
  },
  defaultVariants: { width: 'md' },
});

export type PublicShellRecipeProps = VariantProps<typeof publicShellRecipe>;
export type PublicShellBodyRecipeProps = VariantProps<
  typeof publicShellBodyRecipe
>;

/** Outer page container: max width and the padding a screen sits in. */
/**
 * The frame a screen shares with every other screen: a column, with one gap
 * between its sections and a header at the top.
 *
 * Deliberately no max-width and no padding. DashboardLayout's `<main>` already
 * supplies both and caps nothing on purpose, so a page that adds its own ends
 * up double-padded in a narrower column than every other tab — which is what
 * the Rates tab was doing.
 */
export const pageRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex w-full min-w-0 flex-col'],
  {
    variants: {
      gap: gapVariants,
      /**
       * Fill the height the shell gives instead of growing with content.
       *
       * The header keeps its natural height and the content takes the rest.
       * `min-h-0` is the part people forget: without it a scrolling child
       * stretches the page instead of scrolling inside it.
       */
      fill: {
        true: 'h-full min-h-0',
        false: '',
        /**
         * Fill from `lg` up only. The inspections and trucks screens put a
         * list beside its record, and each column scrolls on its own on a
         * desktop; below `lg` the columns stack and the page scrolls as one.
         */
        lg: 'lg:h-full lg:min-h-0',
      },
    },
    defaultVariants: { gap: 6, fill: false },
  }
);

/** A horizontal rule used to separate sections. */
export const dividerRecipe = cva(
  [uiMarker, uiMarkerLayout, 'shrink-0 border-border'],
  {
    variants: {
      orientation: {
        horizontal: 'w-full border-t',
        vertical: 'h-full border-l self-stretch',
      },
    },
    defaultVariants: { orientation: 'horizontal' },
  }
);

export type StackRecipeProps = VariantProps<typeof stackRecipe>;
export type InlineRecipeProps = VariantProps<typeof inlineRecipe>;
export type PageRecipeProps = VariantProps<typeof pageRecipe>;

/**
 * A responsive column grid.
 *
 * `cols` is the count at desktop; it collapses to one column below `md`
 * regardless, because that is what every grid in the product does and getting
 * it wrong produces a two-column form on a phone. Reach for CSS directly if a
 * screen genuinely needs a different breakpoint.
 */
/**
 * Column classes, written out per breakpoint.
 *
 * Tailwind generates CSS by scanning source for literal class names, so
 * `grid-cols-${n}` produces nothing at all. Every combination has to appear
 * here as a string, which is why this is a table rather than a template.
 *
 * The counts are the ones the app actually uses: 1-4 everywhere, plus 6, 7 and
 * 12 for the pricing matrix and the invoice line grid.
 */
const COLS = {
  base: {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
    12: 'grid-cols-12',
  },
  sm: {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
    5: 'sm:grid-cols-5',
    6: 'sm:grid-cols-6',
    7: 'sm:grid-cols-7',
    12: 'sm:grid-cols-12',
  },
  md: {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
    5: 'md:grid-cols-5',
    6: 'md:grid-cols-6',
    7: 'md:grid-cols-7',
    12: 'md:grid-cols-12',
  },
  lg: {
    1: 'lg:grid-cols-1',
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-5',
    6: 'lg:grid-cols-6',
    7: 'lg:grid-cols-7',
    12: 'lg:grid-cols-12',
  },
  xl: {
    1: 'xl:grid-cols-1',
    2: 'xl:grid-cols-2',
    3: 'xl:grid-cols-3',
    4: 'xl:grid-cols-4',
    5: 'xl:grid-cols-5',
    6: 'xl:grid-cols-6',
    7: 'xl:grid-cols-7',
    12: 'xl:grid-cols-12',
  },
} as const;

export type GridColCount = keyof (typeof COLS)['base'];
export type GridCols =
  | GridColCount
  | Partial<Record<keyof typeof COLS, GridColCount>>;

/**
 * Turn `cols` into class names.
 *
 * A plain number means that many columns at every width — it does NOT quietly
 * collapse on a phone. An earlier version did, and that is the same mistake
 * that vertically centred every screen in the app: a component applying
 * something the call site never asked for. 88 grids in the product are fixed
 * on purpose or by accident, and the component cannot tell which; silently
 * restyling all of them would hide the accidents rather than surface them.
 *
 * To collapse, say so: `cols={{ base: 1, md: 2 }}`.
 */
export function gridColumns(cols: GridCols | undefined): string {
  if (cols === undefined) return '';
  if (typeof cols === 'number') return COLS.base[cols] ?? '';
  return (Object.keys(COLS) as (keyof typeof COLS)[])
    .filter((bp) => cols[bp] !== undefined)
    .map((bp) => COLS[bp][cols[bp]!])
    .filter(Boolean)
    .join(' ');
}

/** Everything about a grid except its columns. */
export const gridRecipe = cva([uiMarker, uiMarkerLayout, 'grid'], {
  variants: {
    gap: gapVariants,
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    },
  },
  defaultVariants: { gap: 4 },
});

export const centerRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex w-full items-center justify-center'],
  {
    variants: {
      direction: {
        row: 'flex-row',
        column: 'flex-col',
      },
      /** Vertical breathing room when the parent gives no height. */
      pad: {
        none: '',
        sm: 'py-6',
        md: 'py-12',
        lg: 'py-24',
      },
      /** Take the parent's height, or the viewport's. */
      fill: {
        none: '',
        parent: 'h-full',
        screen: 'min-h-screen',
      },
      gap: gapVariants,
    },
    defaultVariants: {
      direction: 'row',
      pad: 'none',
      fill: 'none',
      gap: 0,
    },
  }
);

export type GridRecipeProps = VariantProps<typeof gridRecipe>;
export type CenterRecipeProps = VariantProps<typeof centerRecipe>;
